import { I18nContext } from 'contexts/I18n/I18nProvider'
import { CloseModalFunction, OpenModalFunction } from 'core/modal/redux/types'
import { useCallback, useContext, useState } from 'react'
import { IPGroup, OnnetOffnetRow } from './redux/reducer'

type OnnetOffnetGridComponentProps = {
  ipRange: Array<OnnetOffnetRow>
  group: IPGroup

  selectEntry: (ipGroup: IPGroup, index: number) => void
  removeEntry: (ipGroup: IPGroup, index: number) => void
  saveEntry: (ipGroup: IPGroup, index: number, entry: string) => void
  saveNewEntry: (ipGroup: IPGroup, entry: string) => void

  openModal: OpenModalFunction
  closeModal: CloseModalFunction
}

type TableTitleMap = {
  [key in IPGroup]: string
}

const tableTitleMap: TableTitleMap = {
  v4: 'IPV4',
  v6: 'IPV6',
}

const initialState = {
  showNewEntry: false,
  entry: '',
}

export default function OnnetOffnetGridComponent({
  group,
  ipRange,
  selectEntry,
  removeEntry,
  saveEntry,
  saveNewEntry,
  openModal,
  closeModal,
}: OnnetOffnetGridComponentProps) {
  const t = useContext(I18nContext)
  const [entry, setEntry] = useState(initialState)
  const keys = ['entry']

  const confirmAndRemove = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>, index: number) => {
    event.preventDefault()
    const command = () => {
      closeModal()
      removeEntry(group, index)
    }

    openModal({
      title: 'modal.title.confirm_entry_exclusion',
      active: false,
      content: 'modal.content.confirm_action',
      execute: command,
    })
  }, [])

  return (
    <div className="container">
      <h3 className="table-title">{tableTitleMap[group]}</h3>
      <div className="table-container" id="div-container">
        <table
          className="table is-fullwidth is-hoverable is-striped tableCollisions"
          id="table-container"
        >
          <thead>
            <tr>
              <th className="table-header-centered">{t('settings.tab_actions')}</th>
              <th>
                <div className="table-header-with-button">
                  {t('connectivity.label_ip_address')}
                  <button className="button is-dark has-margin-right-md button-content" onClick={() => setEntry({ entry: '', showNewEntry: true })}>
                    <span className="icon">
                      <i className="mdi mdi-plus-circle" />
                    </span>
                    <span>{t('onnet_offnet.label_add_entry')}</span>
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {entry.showNewEntry && (
              <tr key={0}>
                <td>
                  <span className="icon has-text-danger">
                    <i
                      className="mdi mdi-24px mdi-delete"
                      style={{ cursor: 'pointer', color: '#d72c2c' }}
                      onClick={() => setEntry(initialState)}
                    />
                  </span>
                  <span className="icon">
                    <i
                      className="mdi mdi-24px mdi-content-save"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        saveNewEntry(group, entry.entry)
                        setEntry(initialState)
                      }}
                    />
                  </span>
                </td>
                {keys &&
                  keys.map((_, indexNew) => {
                    return (
                      <td key={indexNew}>
                        <input
                          value={entry.entry}
                          type="text"
                          onChange={(event) => setEntry((currentState) => ({ ...currentState, entry: event.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </td>
                    )
                  })}
              </tr>
            )}

            {ipRange.length > 0 &&
              ipRange.map((result, indexTR) => {
                if (result.entryStatus !== 'DELETE') {
                  return (
                    <tr key={indexTR}>
                      <td>
                        <span className="icon has-text-danger">
                          <i
                            className="mdi mdi-24px mdi-delete"
                            style={{ cursor: 'pointer', color: '#d72c2c' }}
                            onClick={(e) => confirmAndRemove(e, indexTR)}
                          />
                        </span>
                        <span className="icon">
                          {!result.editable && (
                            <i
                              className="mdi mdi-24px mdi-lead-pencil"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                selectEntry(group, indexTR)
                                setEntry((currentState) => ({ ...currentState, entry: result.entry }))
                              }}
                            />
                          )}
                          {result.editable && (
                            <i
                              className="mdi mdi-24px mdi-content-save"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                saveEntry(group, indexTR, entry.entry)
                                setEntry(initialState)
                              }}
                            />
                          )}
                        </span>
                      </td>
                      {keys &&
                        keys.map((_, indexTD) => {
                          if (result.editable === true) {
                            return (
                              <td key={indexTD}>
                                <input
                                  defaultValue={result.entry}
                                  onChange={(event) => setEntry((currentState) => ({ ...currentState, entry: event.target.value }))}
                                  style={{ width: '100%' }}
                                />
                              </td>
                            )
                          }
                          return (
                            <td
                              key={indexTD}
                              onDoubleClick={() => selectEntry(group, indexTR)}
                              onBlurCapture={() => selectEntry(group, indexTR)}
                            >
                              {result.entry}
                            </td>
                          )
                        })}
                    </tr>
                  )
                }
                return null
              })}
          </tbody>
        </table>
      </div>
      {!ipRange.length &&
        <div className="no-entries-container">
          <p className="has-text-centered">
            {t('onnet_offnet.label_entries_not_found')}
          </p>
        </div>
      }
    </div>
  )
}
